import {getCountryData} from 'countries-list'
import {DialogTrigger} from 'react-aria-components'

import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {isStateProvinceRequiredCountry} from '@/lib/flight/constants'
import HeadTags from '@/lib/head/HeadTags'
import {isCountryCode} from '@/lib/locale/country'
import {REGIONS_MAP} from '@/lib/locale/regions'
import {useAddresses} from '@/lib/payment/hooks/useAddresses'
import {useAddressOperations} from '@/lib/payment/hooks/useAddressOperations'
import type {ShippingInfo} from '@/lib/payment/paymenModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import {AddressDialog} from './AddressDialog'
import style from './Addresses.module.scss'

type DialogState = {type: 'closed'} | {type: 'add'} | {type: 'edit'; index: number}

export default function Addresses(): React.JSX.Element {
  const [dialogState, setDialogState] = useState<DialogState>({type: 'closed'})

  const {data: addresses = {billingInfo: undefined, shippingInfos: []}} = useAddresses()
  const {deleteAddress, setDefaultAddress} = useAddressOperations()

  const handleAddAddress = useCallback((): void => {
    setDialogState({type: 'add'})
  }, [])

  const handleEditAddress = (index: number): void => {
    setDialogState({type: 'edit', index})
  }

  const handleDeleteAddress = (index: number): void => {
    deleteAddress(index)
  }

  const handleMakeDefault = (index: number): void => {
    setDefaultAddress(index)
  }

  const handleDialogClose = useCallback(() => {
    setDialogState({type: 'closed'})
  }, [])

  const getDialogKey = () => {
    if (dialogState.type === 'closed') return 'closed'
    if (dialogState.type === 'add') return 'add-new'
    return `edit-${dialogState.index}`
  }

  return (
    <>
      <HeadTags title='Addresses' />
      <div className={style.AddressPage}>
        <div className={style.Header}>
          <h1 className={style.HeaderTitle}>Your addresses</h1>
          <DialogTrigger
            isOpen={dialogState.type !== 'closed'}
            onOpenChange={isOpen => {
              if (!isOpen) {
                setDialogState({type: 'closed'})
              }
            }}
          >
            <PrimaryButton
              size='lg'
              className={style.AddButton}
              onPress={handleAddAddress}
              aria-label='Add new address'
            >
              + Add address
            </PrimaryButton>
            <AddressDialog
              key={getDialogKey()}
              title={dialogState.type === 'add' ? 'Add new address' : 'Edit your address'}
              editIndex={dialogState.type === 'edit' ? dialogState.index : undefined}
              onClose={handleDialogClose}
            />
          </DialogTrigger>
        </div>
        <div className={style.AddressesContainer} role='region' aria-label='Saved addresses'>
          {addresses.shippingInfos?.map((address: ShippingInfo, index: number) => (
            <div
              key={index}
              className={style.AddressContainer}
              role='group'
              aria-label={`Address ${index + 1}${index === 0 ? ' (Default)' : ''}`}
            >
              <div className={style.AddressFirstRow}>
                <h3>
                  {address.name ?? 'Unnamed Address'}
                  {index === 0 ? ' (Default)' : ''}
                </h3>
                <div className={style.AddressActions}>
                  <button
                    type='button'
                    onClick={() => {
                      handleEditAddress(index)
                    }}
                    aria-label={`Edit ${address.name ?? 'Unnamed'}'s address`}
                  >
                    Edit
                  </button>
                  <span className={style.ButtonSeparator} aria-hidden='true'>
                    |
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      handleDeleteAddress(index)
                    }}
                    aria-label={`Remove ${address.name ?? 'Unnamed'}'s address`}
                  >
                    Remove
                  </button>
                  {index !== 0 && (
                    <>
                      <span className={style.ButtonSeparator} aria-hidden='true'>
                        |
                      </span>
                      <button
                        type='button'
                        onClick={() => {
                          handleMakeDefault(index)
                        }}
                        aria-label={`Set ${address.name ?? 'Unnamed'}'s address as default`}
                      >
                        Set as default
                      </button>
                    </>
                  )}
                </div>
              </div>
              {(() => {
                const countryName = isCountryCode(address.country)
                  ? getCountryData(address.country).name
                  : address.country

                let stateName: string | undefined = address.state
                if (
                  address.state &&
                  isStateProvinceRequiredCountry(address.country) &&
                  address.state in REGIONS_MAP[address.country]
                ) {
                  stateName =
                    REGIONS_MAP[address.country][
                      address.state as keyof (typeof REGIONS_MAP)[typeof address.country]
                    ]
                }

                return (
                  <div className={style.AddressInfo}>
                    <div>
                      <VisuallyHidden>Address line 1: </VisuallyHidden>
                      {address.line1}
                    </div>
                    {address.line2 && (
                      <div>
                        <VisuallyHidden>Address line 2: </VisuallyHidden>
                        {address.line2}
                      </div>
                    )}
                    <div>
                      <VisuallyHidden>City: </VisuallyHidden>
                      {address.city}
                    </div>
                    {stateName && (
                      <div>
                        <VisuallyHidden>State and postal code: </VisuallyHidden>
                        {stateName} {address.postalCode}
                      </div>
                    )}
                    <div>
                      <VisuallyHidden>Country: </VisuallyHidden>
                      {countryName}
                    </div>
                    <div>Phone number: {address.phone ?? 'No phone number provided'}</div>
                  </div>
                )
              })()}
            </div>
          ))}
          {addresses.shippingInfos?.length === 0 && (
            <div className={style.EmptyState} role='status'>
              No saved addresses yet. Add your first address by clicking the "Add address" button.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
